import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useState } from "react";

import { api } from "../api";
import { timeAgo } from "../format";
import { Alert, Card, Field } from "../shell/ui";

interface PriceAlert {
  id: string;
  symbol: string;
  marketType?: "spot" | "futures";
  direction: "above" | "below";
  price: number;
  repeat: boolean;
  note: string;
  active: boolean;
  triggerCount: number;
  triggeredAt: string | null;
  currentPrice: number | null;
}

const fmt = (n: number) =>
  n >= 100 ? n.toLocaleString("en-US", { maximumFractionDigits: 2 }) : String(Number(n.toPrecision(5)));

/**
 * "Tell me when BTC passes 70,000." Prices refresh every half minute on the server;
 * an alert fires once (or every time, if it repeats) and lands in your Alerts inbox and,
 * if push is on, on your devices.
 */
export function PriceAlertsCard() {
  const qc = useQueryClient();
  const [symbol, setSymbol] = useState("");
  const [marketType, setMarketType] = useState<"spot" | "futures">("spot");
  const [direction, setDirection] = useState<"above" | "below">("above");
  const [price, setPrice] = useState("");
  const [repeat, setRepeat] = useState(false);
  const [note, setNote] = useState("");
  const [error, setError] = useState<string | null>(null);

  const alerts = useQuery({
    queryKey: ["alerts", "price"],
    queryFn: () => api<{ items: PriceAlert[] }>("/alerts/price"),
    refetchInterval: 30_000,
  });
  const markets = useQuery({
    queryKey: ["alerts", "markets", marketType, symbol],
    queryFn: () =>
      api<{ items: Array<{ symbol: string; price: number }>; available: boolean }>(
        `/alerts/markets?marketType=${marketType}&q=${encodeURIComponent(symbol)}`,
      ),
    staleTime: 15_000,
  });
  const create = useMutation({
    mutationFn: () =>
      api<{ id: string }>("/alerts/price", {
        method: "POST",
        body: { symbol, marketType, direction, price: Number(price), repeat, note },
      }),
    onSuccess: () => {
      setError(null);
      setPrice("");
      setNote("");
      void qc.invalidateQueries({ queryKey: ["alerts", "price"] });
    },
    onError: (e: Error) => setError(e.message),
  });
  const remove = useMutation({
    mutationFn: (id: string) => api(`/alerts/price/${id}`, { method: "DELETE" }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["alerts", "price"] }),
  });

  const current = markets.data?.items.find((m) => m.symbol === symbol.toUpperCase())?.price;

  return (
    <Card title="Price alerts">
      <div className="stack">
        <form
          className="stack"
          onSubmit={(e) => {
            e.preventDefault();
            create.mutate();
          }}
        >
          <div className="row">
            <Field label="Market type">
              <select
                value={marketType}
                onChange={(e) => setMarketType(e.target.value as "spot" | "futures")}
              >
                <option value="spot">Spot</option>
                <option value="futures">Futures (perpetual)</option>
              </select>
            </Field>
            <Field label="Market">
              <input
                list="alert-markets"
                value={symbol}
                onChange={(e) => setSymbol(e.target.value.toUpperCase())}
                placeholder="BTCUSDT"
                required
              />
              <datalist id="alert-markets">
                {markets.data?.items.map((m) => (
                  <option key={m.symbol} value={m.symbol}>
                    {fmt(m.price)}
                  </option>
                ))}
              </datalist>
            </Field>
            <Field label="When the price goes">
              <select value={direction} onChange={(e) => setDirection(e.target.value as "above" | "below")}>
                <option value="above">above</option>
                <option value="below">below</option>
              </select>
            </Field>
            <Field label={current ? `Level (now ${fmt(current)})` : "Level"}>
              <input
                type="number"
                min="0"
                step="any"
                value={price}
                onChange={(e) => setPrice(e.target.value)}
                required
              />
            </Field>
          </div>
          <Field label="A note for yourself (optional)">
            <input value={note} maxLength={140} onChange={(e) => setNote(e.target.value)} />
          </Field>
          <label className="checkbox">
            <input type="checkbox" checked={repeat} onChange={(e) => setRepeat(e.target.checked)} /> Repeat{" "}
            <span className="muted small">· fire again each time it crosses, after it has moved back</span>
          </label>
          {markets.data && !markets.data.available && (
            <p className="muted small">Prices are not loaded yet. Give it a minute, then try again.</p>
          )}
          {error && <Alert tone="error">{error}</Alert>}
          <div className="row">
            <button
              type="submit"
              className="btn btn--primary"
              disabled={create.isPending || !symbol || !price}
            >
              Add alert
            </button>
          </div>
        </form>

        {alerts.data && alerts.data.items.length > 0 && (
          <ul className="stack">
            {alerts.data.items.map((a) => (
              <li key={a.id} className="row">
                <span>
                  {!a.active && <span className="badge">Fired</span>} <strong>{a.symbol}</strong>{" "}
                  {a.marketType === "futures" && <span className="badge badge--warn">Futures</span>}{" "}
                  {a.direction} {fmt(a.price)}
                  {a.currentPrice !== null && (
                    <span className="muted small"> · now {fmt(a.currentPrice)}</span>
                  )}
                  {a.repeat && <span className="muted small"> · repeats</span>}
                  {a.note && <span className="muted small"> · {a.note}</span>}
                  {!a.active && a.triggeredAt && (
                    <span className="muted small"> · fired {timeAgo(a.triggeredAt)}</span>
                  )}
                </span>
                <button type="button" className="btn btn--sm" onClick={() => remove.mutate(a.id)}>
                  Remove
                </button>
              </li>
            ))}
          </ul>
        )}
      </div>
    </Card>
  );
}
